import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@/hooks/useUser'
import { useLanguage } from '@/hooks/useLanguage'
import { useToastContext } from '@/store/contexts/ToastContext'
import { t } from '@/utils/i18n'
import { isWebView } from '@/utils/inAppPurchase'
import './Login.css'

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { language, toggleLanguage } = useLanguage()
  const { showToast } = useToastContext()
  const { loginWithGoogle, loginWithApple, guestLogin, handleOAuthCallback, loading, error, isAuthenticated } = useUser()

  // OAuth 콜백 처리 (팝업에서 리다이렉트된 경우)
  useEffect(() => {
    // OAuth 콜백 페이지(/auth/callback, /login/oauth2/code/google, /login/oauth2/code/apple)에서는 처리하지 않음
    const currentPath = window.location.pathname
    if (currentPath === '/auth/callback' ||
        currentPath.startsWith('/login/oauth2/code/google') ||
        currentPath.startsWith('/login/oauth2/code/apple')) {
      // OAuthCallback 컴포넌트에서 처리하므로 여기서는 처리하지 않음
      return
    }
    
    // 팝업 창에서 리다이렉트된 경우 처리 (기존 방식 - tempToken 사용)
    const token = searchParams.get('token')
    const error = searchParams.get('error')
    
    // 팝업 창인지 확인 (opener가 있으면 팝업)
    if (window.opener) {
      // /error 페이지로 리다이렉트된 경우 (백엔드 500 에러 등)
      if (currentPath.includes('/error') || currentPath === '/error') {
        const errorMessage = t('error.serverErrorDesc', language)
        const sendError = () => {
          try {
            window.opener.postMessage(
              { type: 'GOOGLE_LOGIN_ERROR', error: errorMessage },
              window.location.origin
            )
            
          } catch (e) {
            console.error('부모 창에 메시지 전송 실패:', e)
          }
        }
        
        // 즉시 전송 및 여러 번 재시도
        sendError()
        setTimeout(sendError, 100)
        setTimeout(sendError, 300)
        setTimeout(sendError, 500)
        setTimeout(sendError, 1000)
        
        // 팝업 닫기 시도
        setTimeout(() => {
          try {
            window.close()
          } catch (e) {
            // 무시
          }
        }, 500)
        return
      }
      
      if (error) {
        console.error('OAuth 에러:', error)
        
        // 구글 계정이 이미 존재하는 경우 특별 처리
        // URL 전체에서도 확인 (백엔드에서 리다이렉트된 경우)
        const currentUrl = window.location.href
        if (error === 'google_account_exists' || 
            error.includes('google_account_exists') ||
            currentUrl.includes('error=google_account_exists')) {
          const errorMessage = t('messages.googleAccountExists', language)
          
          // 부모 창에 에러 메시지 전송
          const sendError = () => {
            try {
              window.opener.postMessage(
                { type: 'GOOGLE_LOGIN_ERROR', error: errorMessage },
                window.location.origin
              )
              
            } catch (e) {
              console.error('부모 창에 메시지 전송 실패:', e)
            }
          }
          
          // 즉시 전송 및 여러 번 재시도
          sendError()
          setTimeout(sendError, 100)
          setTimeout(sendError, 300)
          setTimeout(sendError, 500)
          setTimeout(sendError, 1000)
          
          // 팝업 닫기 시도
          const closeAttempts = [0, 100, 300, 500, 1000]
          closeAttempts.forEach((delay) => {
            setTimeout(() => {
              try {
                window.close()
              } catch (e) {
                // 무시
              }
            }, delay)
          })
          
          // 알림 표시
          setTimeout(() => {
            alert(errorMessage)
          }, 100)
          
          return
        }
        
        // 일반 에러 처리
        // 부모 창에 에러 메시지 전송 (백엔드에서 전달한 에러 메시지 사용)
        const errorMessage = decodeURIComponent(error) || t('messages.loginFailed', language)
        
        // 메시지 전송을 여러 번 시도 (메시지 리스너가 등록되기 전일 수 있음)
        const sendError = () => {
          try {
            window.opener.postMessage(
              { type: 'GOOGLE_LOGIN_ERROR', error: errorMessage },
              window.location.origin
            )
            
          } catch (e) {
            console.error('부모 창에 메시지 전송 실패:', e)
          }
        }
        
        // 즉시 전송
        sendError()
        // 여러 번 재시도
        setTimeout(sendError, 100)
        setTimeout(sendError, 300)
        setTimeout(sendError, 500)
        setTimeout(sendError, 1000)
        // 팝업 창 닫기 (팝업 내부에서 호출하면 작동할 수 있음)
        // 여러 번 시도하여 확실히 닫기
        const closeAttempts = [0, 100, 300, 500, 1000]
        closeAttempts.forEach((delay) => {
          setTimeout(() => {
            try {
              window.close()
            } catch (e) {
              // 무시
            }
          }, delay)
        })
        // 2초 후에도 닫히지 않으면 사용자에게 안내
        setTimeout(() => {
          try {
            if (!window.closed) {
              // 팝업이 아직 열려있으면 사용자에게 안내 메시지 표시
              document.body.innerHTML = `
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; padding: 20px; text-align: center; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
                  <h2 style="color: #d32f2f; margin-bottom: 16px;">${t('messages.loginFailed', language)}</h2>
                  <p style="color: #666; margin-bottom: 24px;">${errorMessage}</p>
                  <p style="color: #999; font-size: 14px; margin-bottom: 16px;">${language === 'ko' ? '이 창을 닫아주세요.' : 'Please close this window.'}</p>
                  <button onclick="window.close()" style="padding: 10px 20px; background: #667eea; color: white; border: none; border-radius: 8px; cursor: pointer; font-size: 14px;">
                    ${t('common.close', language)}
                  </button>
                </div>
              `
            }
          } catch (e) {
            // window.closed 접근이 차단될 수 있음
          }
        }, 2000)
        return
      }
      
      if (token) {
        
        // 부모 창에 토큰 전송 (여러 번 시도)
        const sendSuccess = () => {
          try {
            window.opener.postMessage(
              { type: 'GOOGLE_LOGIN_SUCCESS', token },
              window.location.origin
            )
            
          } catch (e) {
            console.error('부모 창에 메시지 전송 실패:', e)
          }
        }
        
        // 즉시 전송
        sendSuccess()
        // 여러 번 재시도
        setTimeout(sendSuccess, 100)
        setTimeout(sendSuccess, 300)
        setTimeout(sendSuccess, 500)
        setTimeout(sendSuccess, 1000)
        
        // 팝업 창 닫기
        setTimeout(() => {
          try {
            window.close()
          } catch (e) {
            // 무시
          }
        }, 500)
        return
      }
    } else {
      // 일반 페이지에서 리다이렉트된 경우 (기존 방식 유지)
      if (error) {
        console.error('OAuth 에러:', error)
        
        // 구글 계정이 이미 존재하는 경우 특별 처리
        const currentUrl = window.location.href
        if (error === 'google_account_exists' || 
            error.includes('google_account_exists') ||
            currentUrl.includes('error=google_account_exists')) {
          const errorMessage = t('messages.googleAccountExists', language)
          showToast(errorMessage, 'error')
          window.history.replaceState({}, '', '/login')
          return
        }
        
        showToast(
          t('messages.loginFailed', language) || '구글 로그인에 실패했습니다.',
          'error'
        )
        window.history.replaceState({}, '', '/login')
        return
      }
      
      if (token) {
        
        handleOAuthCallback(token)
          .then(() => {
            navigate('/', { replace: true })
            window.history.replaceState({}, '', '/login')
          })
          .catch((err) => {
            console.error('OAuth 콜백 처리 실패:', err)
            showToast(
              t('messages.loginFailed', language) || '로그인에 실패했습니다.',
              'error'
            )
          })
      }
    }
  }, [searchParams, handleOAuthCallback, navigate, showToast, language])

  // 이미 로그인된 사용자는 대시보드로 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle()
    } catch (err) {
      console.error('구글 로그인 시작 실패:', err)
    }
  }

  const handleGuestLogin = async () => {
    try {
      await guestLogin()
      navigate('/', { replace: true })
    } catch (err: unknown) {
      console.error('게스트 로그인 실패:', err)
      showToast(
        t('messages.loginFailed', language) || '로그인에 실패했습니다.',
        'error'
      )
    }
  }

  return (
    <div className="login-page">
      <div className="login-page__background">
        <div className="login-page__gradient"></div>
      </div>
      <div className="login-page__container">
        <div className="login-page__card">
          <div className="login-page__header">
            <div className="login-page__header-top">
              <button
                className="login-page__language-toggle"
                onClick={toggleLanguage}
                type="button"
                aria-label={t('userManagement.changeLanguage', language)}
              >
                {language === 'ko' ? '🇰🇷' : '🇺🇸'} {language.toUpperCase()}
              </button>
            </div>
            <div className="login-page__brand">
              <h1 className="login-page__title">Dreamity</h1>
              <span className="login-page__subtitle">Planner</span>
            </div>
            <p className="login-page__description">
              {(t('userManagement.loginDescription', language) || '꿈을 이루기 위한 여정을 시작하세요.\n구글 계정으로 로그인하세요.').split('\n').map((line, index, array) => (
                <span key={index}>
                  {line}
                  {index < array.length - 1 && <br />}
                </span>
              ))}
            </p>
          </div>

          {error && !error.includes('이미 존재하는 구글 계정') && (
            <div className="login-page__error">
              <p>{error}</p>
            </div>
          )}

          <div className="login-page__form">
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="login-page__google-button"
            >
              <svg
                className="login-page__google-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span className="login-page__google-text">
                {t('userManagement.loginWithGoogle', language) || '구글로 로그인'}
              </span>
            </button>

            {isWebView() && (
              <>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await loginWithApple()
                    } catch (err) {
                      console.error('Apple 로그인 시작 실패:', err)
                    }
                  }}
                  disabled={loading}
                  className="login-page__apple-button"
                >
                  <svg
                    className="login-page__apple-icon"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span className="login-page__apple-text">
                    {t('userManagement.loginWithApple', language) || 'Apple로 로그인'}
                  </span>
                </button>
              </>
            )}

            <button
              type="button"
              onClick={handleGuestLogin}
              disabled={loading}
              className="login-page__guest-button"
            >
              <span className="login-page__guest-icon" aria-hidden>👤</span>
              <span className="login-page__guest-text">
                {t('userManagement.guestLogin', language) || '게스트로 둘러보기'}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
